import { FC, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { AnimatePresence, motion } from 'motion/react';
import { StanButton, StanProgressBar } from '@/components';
import { SocialInput } from '@/features/profile';
import { getProgressPercentage } from '@/features/subscriptions/utils/subscriptionFlags';
import { useSocialLinksForm, useSubscriptionFlags } from '@/features/subscriptions/hooks';
import { trackFacebookEvent, trackTikTokEvent } from '@/lib/adsTracking';
import { stanAnalytics } from '@/lib/stanAnalytics';
import { SocialPlatform } from '@/features/profile/types/profile.types';
import { getSocialPlatformConfig } from '@/features/profile/constants/socialPlatforms';
import IcChevronDown from '@/assets/icons/ic-chevron-down.svg';
import IcChevronUp from '@/assets/icons/ic-chevron-up.svg';

// Default fields shown initially (matching Vue)
const DEFAULT_FIELDS: SocialPlatform[] = ['instagram', 'tiktok', 'linkedin'];

// Extended fields shown when "View more" is clicked
const EXTENDED_FIELDS: SocialPlatform[] = [
    'email',
    'facebook',
    'youtube',
    'link',
    'pinterest',
    'twitter',
    'spotify',
    'apple-podcasts',
    'etsy',
    'discord',
    'snapchat',
    'twitch',
    'vimeo',
];

const SubscribeSocialsPage: FC = () => {
    const { add_socials_before_subscription } = useSubscriptionFlags();
    const { control, showMore, onSubmit, handleSkip, toggleShowMore } = useSocialLinksForm();

    const progressPercentage = getProgressPercentage('socials', !!add_socials_before_subscription);

    useEffect(() => {
        trackFacebookEvent('PageView');
        trackTikTokEvent('PageView');
        stanAnalytics('subscribe-socials-page-view');
    }, []);

    const renderSocialInput = (platform: SocialPlatform) => {
        const config = getSocialPlatformConfig(platform);
        if (!config) return null;

        return (
            <Controller
                key={platform}
                name={`socials.${platform}`}
                control={control}
                render={({ field, fieldState }) => (
                    <SocialInput
                        id={`social-${platform}`}
                        name={field.name}
                        platform={platform}
                        value={field.value || ''}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        error={!!fieldState.error}
                        hintText={fieldState.error?.message}
                    />
                )}
            />
        );
    };

    return (
        <div className='flex min-h-[calc(100svh-4rem)] flex-col'>
            {/* Progress Bar */}
            <div className='mx-auto w-1/2 pb-7'>
                <StanProgressBar
                    value={progressPercentage}
                    min={0}
                    max={100}
                    progressColor='#a286ff'
                    barColor='#eef1f5'
                />
            </div>

            {/* Main Container */}
            <div className='mx-auto flex w-full max-w-sm flex-1 flex-col px-8'>
                {/* Title */}
                <div className='mb-5 text-center'>
                    <h1 className='text-heading-h2 text-dark'>Connect your social accounts to your store</h1>
                </div>

                {/* Social Inputs */}
                <div className='mt-5 mb-5'>
                    {/* Default Fields */}
                    <div className='mb-2.5 space-y-2.5'>
                        {DEFAULT_FIELDS.map(platform => renderSocialInput(platform))}
                    </div>

                    {/* View More Toggle */}
                    <div
                        className='my-7.5 flex cursor-pointer items-center justify-center gap-3'
                        onClick={toggleShowMore}
                    >
                        <span className='text-body-sm text-brand-main font-medium'>
                            {showMore ? 'View less' : 'View more'}
                        </span>
                        {showMore ? (
                            <IcChevronUp className='text-brand-main size-4' />
                        ) : (
                            <IcChevronDown className='text-brand-main size-4' />
                        )}
                    </div>

                    {/* Extended Fields with Animation */}
                    <AnimatePresence>
                        {showMore && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.3, ease: 'easeInOut' }}
                                className='space-y-2.5 overflow-hidden'
                            >
                                {EXTENDED_FIELDS.map(platform => renderSocialInput(platform))}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Buttons - pinned to bottom */}
                <div className='mt-auto flex flex-col pb-0'>
                    <StanButton name='subscribe-socials-continue' type='button' color='primary' onClick={onSubmit}>
                        Next
                    </StanButton>
                    <StanButton name='subscribe-socials-skip' type='button' variant='ghost' onClick={handleSkip}>
                        Skip
                    </StanButton>
                </div>
            </div>
        </div>
    );
};

export default SubscribeSocialsPage;
